import { useCallback, useEffect, useMemo } from 'react';
export const _channels = new Map();
export const _createBroadcastChannel = (channelName) => {
    try {
        return new BroadcastChannel(channelName);
    }
    catch (error) {
        return null;
    }
};
const initChannel = (channelName) => {
    const existingChannel = _channels.get(channelName);
    if (existingChannel) {
        return existingChannel;
    }
    const broadcastChannel = _createBroadcastChannel(channelName);
    if (!broadcastChannel) {
        return null;
    }
    const newChannel = {
        channel: broadcastChannel,
        updateListeners: new Set(),
        errorListeners: new Set(),
    };
    _channels.set(channelName, newChannel);
    newChannel.channel.onmessage = (e) => newChannel.updateListeners.forEach((fn) => fn(e.data));
    newChannel.channel.onmessageerror = (e) => {
        newChannel.errorListeners.forEach((fn) => fn(e));
    };
    return newChannel;
};
/** * Enum for channel names. */
export const ChannelName = {
    PROFILE_PAGES_UPDATE: 'profile_pages_update',
};
/**
 * A custom hook for sending information between different tabs and windows.
 * Allows us to seamlessly change info in one tab and receive it in another tab/window.
 * @param onUpdate - When another tab/window sends a message, this is called with the info received
 * @param onFailure - Called when broadcast channels fails.
 * @returns The function that broadcasts a message to other tabs/windows.
 */
export const useUpdateBroadcastChannel = (channelName, onUpdate, onFailure) => {
    const broadcastChannel = useMemo(() => initChannel(channelName), [channelName]);
    useEffect(() => {
        if (onUpdate) {
            broadcastChannel === null || broadcastChannel === void 0 ? void 0 : broadcastChannel.updateListeners.add(onUpdate);
            return () => {
                broadcastChannel === null || broadcastChannel === void 0 ? void 0 : broadcastChannel.updateListeners.delete(onUpdate);
            };
        }
        return () => { };
    }, [onUpdate, broadcastChannel === null || broadcastChannel === void 0 ? void 0 : broadcastChannel.updateListeners]);
    useEffect(() => {
        if (onFailure) {
            broadcastChannel === null || broadcastChannel === void 0 ? void 0 : broadcastChannel.errorListeners.add(onFailure);
            return () => {
                broadcastChannel === null || broadcastChannel === void 0 ? void 0 : broadcastChannel.errorListeners.delete(onFailure);
            };
        }
        return () => { };
    }, [onFailure, broadcastChannel === null || broadcastChannel === void 0 ? void 0 : broadcastChannel.errorListeners]);
    return useCallback((message) => broadcastChannel === null || broadcastChannel === void 0 ? void 0 : broadcastChannel.channel.postMessage(message), [broadcastChannel === null || broadcastChannel === void 0 ? void 0 : broadcastChannel.channel]);
};
